/**
 * showdownMoves.batch9 — batch 9 of the per-move Showdown animation port.
 *
 * Each entry is a near-verbatim port of the move's script from Pokémon Showdown's
 * `BattleMoveAnims` (`battle-animations-moves.ts`, MIT/CC0). Our {@link FxScene} +
 * {@link Actor} mirror Showdown's `scene` + `Sprite`, so the literal
 * `scene.showEffect(...)` / `attacker.anim(...)` / `scene.wait(...)` sequences port
 * unchanged. Moves that delegated to a shared `BattleOtherAnims` template reuse the
 * ported template from {@link module:showdownOther}; moves that delegated to another
 * move (the alias assignments at the tail of the PS file, e.g.
 * `BattleMoveAnims['twister'] = { anim: BattleMoveAnims['whirlwind'].anim }`) reuse
 * the locally-ported target body here.
 *
 * Effect-name discipline: only effect keys vendored in our fx catalog are used.
 * Where Showdown referenced an effect we did not vendor:
 *   - `attacker.sp` (the attacker's own sprite drawn as a streak) is not vendored —
 *     those `showEffect(attacker.sp, …)` calls are OMITTED (same treatment as
 *     `fastattack`/`fastanimattack` in showdownOther.ts); the attacker's lunge
 *     preserves the dash read. Affects wildcharge, trumpcard (gigaimpact), waterfall
 *     (aquajet).
 *   - remote/gradient backgrounds (`wish`'s bg-space.jpg url, `wavecrash`'s
 *     `linear-gradient(...)`) are substituted with a solid `backgroundEffect` colour
 *     wash, noted inline.
 * The motion (the `actor.anim` choreography) is always preserved verbatim.
 *
 * Test/runtime separation: imports ONLY TYPES from `showdownFx.ts` (that file pulls
 * in Phaser, which can't load in the node test env). Every script is a pure function
 * over the `FxScene`/`Actor` interfaces.
 */
import type { FxScene, Actor, MoveAnim } from './showdownFx.js';
import { contactattack, fastattack } from './showdownOther.js';

/* ──────────────────────────────────────────────────────────────────────────
 * Local template ports (PS `BattleOtherAnims` entries used by several scripts).
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `BattleOtherAnims.bite` — the two jaws snapping shut on the defender. */
const bite: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'topbite',
    { x: defender.x, y: defender.y + 50, z: defender.z, scale: 0.5, opacity: 0, time: 370 },
    { x: defender.x, y: defender.y + 10, z: defender.z, scale: 0.5, opacity: 1, time: 500 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'bottombite',
    { x: defender.x, y: defender.y - 50, z: defender.z, scale: 0.5, opacity: 0, time: 370 },
    { x: defender.x, y: defender.y - 10, z: defender.z, scale: 0.5, opacity: 1, time: 500 },
    'linear',
    'fade',
  );
};

/** PS `BattleOtherAnims.hydroshot` — three water wisps spray onto the defender. */
const hydroshot: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3 },
    { x: defender.x + 10, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 75 },
    { x: defender.x - 10, y: defender.y - 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 150 },
    { x: defender.x, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
};

/** PS `BattleOtherAnims.sneakattack` — fade out, dart through, strike from behind. */
const sneakattack: MoveAnim = (_scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: attacker.leftof(-20), y: attacker.y, z: attacker.behind(-20), opacity: 0, time: 200 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-120), opacity: 0, time: 1 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(40), opacity: 1, time: 250 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-5), opacity: 0, time: 300 },
    'linear',
  );
  attacker.anim({ opacity: 0, time: 1 }, 'linear');
  attacker.anim({ time: 300, opacity: 1 }, 'linear');
  defender.delay(330);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `BattleOtherAnims.dance` — a small side-to-side sway on the user. */
const dance: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10 });
  attacker.anim({ x: attacker.x + 10 });
  attacker.anim({ x: attacker.x });
};

/** PS `BattleOtherAnims.spinattack` — a spinning ballistic lunge into the defender. */
const spinattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: defender.x, y: defender.y + 60, z: defender.behind(-30), time: 400 },
    'ballistic2',
  );
  attacker.anim({ x: defender.x, y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
  scene.wait(500);
};

/** PS `BattleOtherAnims.selfstatus` — two wisps collapse inward on the user. */
const selfstatus: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 0 },
    { scale: 0, opacity: 1, time: 300 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 200 },
    { scale: 0, opacity: 1, time: 500 },
    'linear',
  );
};

/** PS `BattleOtherAnims.sound` — three wisp rings expand off the user. */
const sound: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 0 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 150 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 300 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 800 },
    'linear',
  );
};

/** PS `BattleOtherAnims.shake` — a quick side-to-side shudder on the user. */
const shake: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10, time: 200 });
  attacker.anim({ x: attacker.x + 10, time: 300 });
  attacker.anim({ x: attacker.x, time: 200 });
};

/** PS `BattleOtherAnims.xattack` — two wisp bursts + a double ballistic strike. */
const xattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 700 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 1000,
    },
    'linear',
  );
  defender.delay(480);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
};

/* ──────────────────────────────────────────────────────────────────────────
 * Custom move scripts.
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `terrainpulse` — a wide icy pulse off the user + radial wisp spokes. */
const terrainpulse: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  const xf = [1, -1, 1, -1];
  const yf = [1, -1, -1, 1];
  const xf2 = [1, 0, -1, 0];
  const yf2 = [0, 1, 0, -1];

  scene.showEffect(
    'iceball',
    {
      x: attacker.x,
      y: attacker.y - 50,
      z: attacker.z,
      scale: 1,
      xscale: 5,
      opacity: 0.8,
      time: 0,
    },
    { scale: 2, xscale: 8, opacity: 0.1, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'iceball',
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.3, scale: 0, time: 300 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'iceball',
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.3, scale: 0, time: 500 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0, time: 800 },
    'linear',
  );
  for (let i = 0; i < 4; i++) {
    scene.showEffect(
      'wisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.4 },
      {
        x: attacker.x + 240 * xf[i],
        y: attacker.y,
        z: attacker.z + 137 * yf[i],
        scale: 0.7,
        opacity: 0.4,
        time: 600,
      },
      'accel',
      'fade',
    );
    scene.showEffect(
      'wisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.2, opacity: 0.4 },
      {
        x: attacker.x + 339 * xf2[i],
        y: attacker.y,
        z: attacker.z + 194 * yf2[i],
        scale: 0.5,
        opacity: 0.4,
        time: 600,
      },
      'accel',
      'fade',
    );
  }
};

/** PS `thief` → `sneakattack` template. */
const thief: MoveAnim = sneakattack;

/** PS `thrash` — a furious flurry of stomps and punches over a wild double-lunge. */
const thrash: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'angry',
    { x: attacker.x - 10, y: attacker.y + 50, z: attacker.z, scale: 0.5, opacity: 1, time: 0 },
    { scale: 3, opacity: 0, time: 300 },
    'ballistic2Under',
    'fade',
  );

  attacker.delay(300);
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 300 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(750);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(15), time: 300 }, 'decel');
  defender.anim({ time: 300 }, 'swing');

  scene.showEffect(
    'foot',
    { x: defender.x - 10, y: defender.y + 20, z: defender.z, scale: 1, opacity: 1, time: 650 },
    {
      x: defender.x - 15,
      y: defender.y + 10,
      z: defender.behind(15),
      scale: 2,
      opacity: 0,
      time: 950,
    },
    'linear',
  );
  scene.showEffect(
    'fist',
    { x: defender.x - 5, y: defender.y - 5, z: defender.z, scale: 0.6, opacity: 0.6, time: 675 },
    { x: defender.x - 10, y: defender.y - 10, z: defender.z, scale: 1, opacity: 1, time: 875 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x + 10, y: defender.y + 10, z: defender.z, scale: 0.6, opacity: 0.6, time: 700 },
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 1, opacity: 1, time: 900 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 0.6, opacity: 0.6, time: 725 },
    { x: defender.x + 30, y: defender.y - 25, z: defender.z, scale: 1, opacity: 1, time: 925 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'foot',
    { x: defender.x, y: defender.y + 20, z: defender.z, scale: 1, opacity: 1, time: 1000 },
    { x: defender.x, y: defender.y + 10, z: defender.behind(15), scale: 2, opacity: 0, time: 1300 },
    'linear',
  );
};

/** PS `throatchop` → alias of `karatechop` — a chop mark + a contact lunge. */
const throatchop: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'rightchop',
    {
      x: defender.leftof(30),
      y: defender.y + 50,
      z: defender.behind(-10),
      scale: 0.6,
      opacity: 1,
      time: 475,
    },
    { y: defender.y - 20, opacity: 0.5, time: 550 },
    'linear',
    'fade',
  );
  contactattack(scene, actors);
};

/** PS `thunderfang` — an electro orb + lightning, then bite + a contact lunge. */
const thunderfang: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 450 },
    { x: defender.x, y: defender.y - 40, z: defender.behind(15), scale: 3, opacity: 0, time: 800 },
    'linear',
  );
  scene.showEffect(
    'lightning',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 550 },
    { x: defender.x, y: defender.y - 40, z: defender.behind(15), scale: 3, opacity: 0, time: 900 },
    'linear',
  );
  bite(scene, actors);
  contactattack(scene, actors);
};

/** PS `thundershock` → alias of `electroball` — a single electro orb travels + zaps. */
const thundershock: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.3 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0.6, time: 500 },
    'linear',
    'explode',
  );
  defender.delay(500);
  defender.anim({ z: defender.behind(5), time: 200 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `thunderwave` — three electro rings ripple out toward the defender. */
const thunderwave: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.2, time: 0 },
    { scale: 8, opacity: 0.1, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.2, time: 200 },
    { scale: 8, opacity: 0.1, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0.2, time: 500 },
    { scale: 4, opacity: 0.1, time: 800 },
    'linear',
    'fade',
  );
};

/** PS `tickle` — the defender giggles side to side. */
const tickle: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  defender.anim({ x: defender.x - 10, time: 75 });
  defender.anim({ x: defender.x + 10, time: 100 });
  defender.anim({ x: defender.x - 10, time: 100 });
  defender.anim({ x: defender.x + 10, time: 100 });
  defender.anim({ x: defender.x, time: 100 });
  scene.wait(425);
};

/** PS `tidyup` → alias of `bulkup` — a charging wisp + a bracing scale-up. */
const tidyup: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y + 20, z: attacker.z, scale: 0.5, opacity: 0.1, time: 500 },
    {
      x: attacker.leftof(-30),
      y: attacker.y + 20,
      z: attacker.behind(-50),
      scale: 1,
      opacity: 0.3,
      time: 800,
    },
    'ballistic2Under',
    'fade',
  );
  attacker.anim({ y: attacker.y + 4, scale: 1.15, time: 200 }, 'linear');
  attacker.delay(300);
  attacker.anim({ time: 200 }, 'decel');
};

/** PS `toxic` — a poison glob lobs onto the defender and bursts. */
const toxic: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 400 },
    'ballistic',
    'explode',
  );
};

/** PS `toxicspikes` — two poison caltrops scatter onto the defender's side. */
const toxicspikes: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'poisoncaltrop',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5 },
    { x: +5, y: defender.y - 40, z: defender.z, scale: 0.3, opacity: 1 },
    'ballistic',
  );
  scene.showEffect(
    'poisoncaltrop',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5, time: 200 },
    { x: -15, y: defender.y - 35, z: defender.z, scale: 0.3, opacity: 1 },
    'ballistic',
  );
};

/** PS `toxicthread` → alias of `electroweb` — three sticky web globs splatter. */
const toxicthread: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'web',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 400 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'web',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 100 },
    { x: defender.x + 40, y: defender.y - 20, z: defender.z, scale: 0.5, opacity: 1, time: 500 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'web',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 200 },
    { x: defender.x - 30, y: defender.y - 10, z: defender.z, scale: 0.5, opacity: 1, time: 600 },
    'ballistic',
    'explode',
  );
};

/** PS `powerwhip` body — energy orbs + a flurry of leaves + a contact lunge. */
const powerwhip: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 420 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 520 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  scene.showEffect(
    'leaf1',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { x: defender.x, y: defender.y - 60, scale: 1.5, opacity: 0, time: 1100 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leaf2',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { x: defender.x + 60, y: defender.y, scale: 1.5, opacity: 0, time: 1100 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leaf2',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { x: defender.x, y: defender.y + 60, scale: 1.5, opacity: 0, time: 1100 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leaf1',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { x: defender.x - 60, y: defender.y, scale: 1.5, opacity: 0, time: 1100 },
    'linear',
    'fade',
  );
  contactattack(scene, actors);
};

/** PS `trailblaze` → alias of `powerwhip`. */
const trailblaze: MoveAnim = powerwhip;

/** PS `transform` — no animation in Showdown (the sprite swap is handled elsewhere). */
const transform: MoveAnim = () => {};

/** PS `triattack` — fire/ice/electro orbs gather, stream out, and burst in sequence. */
const triattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y + 45, z: attacker.z, scale: 0, opacity: 0.2 },
    { scale: 0.5, opacity: 0.6, time: 400 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x - 45, y: attacker.y - 20, z: attacker.z, scale: 0, opacity: 0.2, time: 100 },
    { scale: 0.5, opacity: 0.6, time: 500 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x + 45, y: attacker.y - 20, z: attacker.z, scale: 0, opacity: 0.2, time: 200 },
    { scale: 0.5, opacity: 0.6, time: 600 },
    'decel',
    'fade',
  );

  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y + 45, z: attacker.z, scale: 0.5, opacity: 0.6, time: 400 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(5), opacity: 0.8, time: 700 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x - 45, y: attacker.y - 20, z: attacker.z, scale: 0.5, opacity: 0.6, time: 500 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(5), opacity: 0.8, time: 800 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x + 45, y: attacker.y - 20, z: attacker.z, scale: 0.5, opacity: 0.6, time: 600 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(5), opacity: 0.8, time: 900 },
    'accel',
    'explode',
  );

  scene.showEffect(
    'fireball',
    { x: defender.x - 15, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.8, time: 600 },
    { scale: 3, opacity: 0, time: 900 },
    'linear',
  );
  scene.showEffect(
    'lightning',
    { x: defender.x + 15, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.8, time: 700 },
    { scale: 5, opacity: 0, time: 1000 },
    'linear',
  );
  scene.showEffect(
    'icicle',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.8, time: 800 },
    { scale: 3, opacity: 0, time: 1100 },
    'linear',
  );

  defender.delay(675);
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 150 }, 'swing');
};

/** PS `trick` — pokeballs juggle back and forth between the two combatants. */
const trick: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'pokeball',
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 0 },
    { x: defender.x, y: defender.y - 30, z: defender.z, time: 300 },
    'ballistic2',
    'fade',
  );
  scene.showEffect(
    'pokeball',
    { x: defender.x, y: defender.y, z: defender.z, time: 0 },
    { x: attacker.x, y: attacker.y - 30, z: attacker.z, time: 300 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'pokeball',
    { x: defender.x, y: defender.y - 30, z: defender.z, time: 300 },
    { x: attacker.x - 50, y: attacker.y, z: attacker.z, time: 600 },
    'ballistic2',
    'fade',
  );
  scene.showEffect(
    'pokeball',
    { x: attacker.x, y: attacker.y - 30, z: attacker.z, time: 300 },
    { x: defender.x - 50, y: defender.y, z: defender.z, time: 600 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'pokeball',
    { x: attacker.x - 50, y: attacker.y, z: attacker.z, time: 600 },
    { x: defender.x, y: defender.y, z: defender.z, time: 900 },
    'ballistic2',
    'explode',
  );
  scene.showEffect(
    'pokeball',
    { x: defender.x - 50, y: defender.y, z: defender.z, time: 600 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 900 },
    'ballistic2Under',
    'explode',
  );
};

/** PS `trickroom` → `dance` template. */
const trickroom: MoveAnim = dance;

/** PS `tripleaxel` — icicles + a pair of kicks, then the X-attack flurry. */
const tripleaxel: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'icicle',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'icicle',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 500 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  scene.showEffect(
    'foot',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 450 },
    {
      x: defender.leftof(20),
      y: defender.y,
      z: defender.behind(20),
      scale: 2,
      opacity: 0,
      time: 750,
    },
    'linear',
  );
  scene.showEffect(
    'foot',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 750 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 2,
      opacity: 0,
      time: 1050,
    },
    'linear',
  );
  xattack(scene, actors);
};

/** PS `triplekick` → alias of `doublekick` — two foot strikes + the X-attack flurry. */
const triplekick: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'foot',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 450 },
    {
      x: defender.leftof(20),
      y: defender.y,
      z: defender.behind(20),
      scale: 2,
      opacity: 0,
      time: 750,
    },
    'linear',
  );
  scene.showEffect(
    'foot',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 750 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 2,
      opacity: 0,
      time: 1050,
    },
    'linear',
  );
  xattack(scene, actors);
};

/**
 * PS `trumpcard` → alias of `gigaimpact` — a black wash + impact bursts + a heavy
 * ramming lunge. PS drew two `attacker.sp` sprite-streaks (not vendored) — omitted;
 * the attacker's accel lunge preserves the ram read.
 */
const trumpcard: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 700, 0.2);
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 300 },
    { scale: 4, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 500 },
    { scale: 4, opacity: 0, time: 800 },
    'linear',
  );
  // PS drew two attacker-sprite streaks here (showEffect(attacker.sp, …)); omitted.
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `twinbeam` — alternating mistball/poisonwisp pulses stream onto the defender. */
const twinbeam: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'mistball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2 },
    { x: defender.x, y: defender.y, z: defender.behind(20), opacity: 0.6, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 50 },
    { x: defender.x + 10, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 250 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'mistball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 100 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(20), opacity: 0.6, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 150 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 350 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'mistball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2 },
    { x: defender.x, y: defender.y, z: defender.behind(20), opacity: 0.6, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 450 },
    { x: defender.x + 10, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 700 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'mistball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 550 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(20), opacity: 0.6, time: 750 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 600 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 800 },
    'linear',
    'explode',
  );
};

/** PS `twineedle` — two energy needles jab into the defender and burst. */
const twineedle: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6 },
    { x: defender.x - 35, y: defender.y + 10, z: defender.z, opacity: 0.6, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6, time: 200 },
    { x: defender.x + 20, y: defender.y - 20, z: defender.z, opacity: 0.6, time: 500 },
    'linear',
    'explode',
  );
};

/** PS `twister` → alias of `whirlwind` — three rings of wisps swirl around the target. */
const twister: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  for (let i = 0; i < 3; i++) {
    scene.showEffect(
      'wisp',
      {
        x: defender.x + 30,
        y: defender.y - 35,
        z: defender.behind(i * 40 - 60),
        scale: 0.2,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x - 30,
        y: defender.y,
        z: defender.behind(i * 40 - 60),
        scale: 0.4,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'wisp',
      {
        x: defender.x - 30,
        y: defender.y + 35,
        z: defender.behind(i * 40 - 60),
        scale: 0.2,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x + 30,
        y: defender.y,
        z: defender.behind(i * 40 - 60),
        scale: 0.4,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'wisp',
      {
        x: defender.x + 30,
        y: defender.y,
        z: defender.behind(i * 40 - 60),
        scale: 0.2,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x - 30,
        y: defender.y - 35,
        z: defender.behind(i * 40 - 60),
        scale: 0.4,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'wisp',
      {
        x: defender.x - 30,
        y: defender.y,
        z: defender.behind(i * 40 - 60),
        scale: 0.2,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x + 30,
        y: defender.y - 35,
        z: defender.behind(i * 40 - 60),
        scale: 0.4,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
  }
};

/** PS `uturn` → `spinattack` template. */
const uturn: MoveAnim = spinattack;

/** PS `upperhand` — a quick dash + a rising chop mark on the defender. */
const upperhand: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  const chopDirection = defender.isFrontSprite ? 'leftchop' : 'rightchop';

  fastattack(scene, actors);
  scene.showEffect(
    chopDirection,
    {
      x: defender.leftof(20),
      y: defender.y + 10,
      z: defender.behind(-15),
      scale: 0.5,
      opacity: 0.5,
      time: 0,
    },
    { time: 200 },
    'linear',
    'fade',
  );
  scene.showEffect(
    chopDirection,
    {
      x: defender.leftof(20),
      y: defender.y + 10,
      z: defender.behind(-15),
      scale: 0.5,
      opacity: 0.5,
      time: 200,
    },
    { z: defender.behind(10), opacity: 0.8, time: 300 },
    'decel',
    'fade',
  );
  scene.showEffect(
    chopDirection,
    {
      x: defender.leftof(20),
      y: defender.y + 10,
      z: defender.behind(10),
      scale: 0.5,
      opacity: 0.8,
      time: 300,
    },
    { opacity: 0.5, time: 400 },
    'linear',
    'fade',
  );
};

/** PS `uproar` → alias of `hypervoice` — three loud iceball rings blast off the user. */
const uproar: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.5, time: 0 },
    { z: attacker.behind(-50), scale: 7, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.5, time: 150 },
    { z: attacker.behind(-50), scale: 7, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.5, time: 300 },
    { z: attacker.behind(-50), scale: 7, opacity: 0, time: 800 },
    'linear',
  );
};

/** PS `vacuumwave` — a fist of compressed air rushes into the defender. */
const vacuumwave: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'fist',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 1 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, time: 200 },
    'accel',
    'fade',
  );
};

/** PS `venomdrench` → alias of `sludge` — three poison globs lob and fade. */
const venomdrench: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.7, opacity: 1, time: 400 },
    'ballistic',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 100 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.7, opacity: 1, time: 500 },
    'ballistic',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 200 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.7, opacity: 1, time: 600 },
    'ballistic',
    'fade',
  );
};

/** PS `venoshock` → alias of `sludgebomb` — three poison globs splatter on the defender. */
const venoshock: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.7, opacity: 1, time: 400 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 100 },
    { x: defender.x + 40, y: defender.y - 20, z: defender.z, scale: 0.7, opacity: 1, time: 500 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 200 },
    { x: defender.x - 30, y: defender.y - 10, z: defender.z, scale: 0.7, opacity: 1, time: 600 },
    'ballistic',
    'explode',
  );
};

/** PS `vinewhip` → alias of `powerwhip`. */
const vinewhip: MoveAnim = powerwhip;

/** PS `vitalthrow` → alias of `circlethrow` — wisps + a chop, then a grappling throw. */
const vitalthrow: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    { x: defender.leftof(-20), z: defender.behind(20), scale: 3, opacity: 0, time: 700 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 500 },
    { x: defender.leftof(-20), z: defender.behind(20), scale: 0.6, opacity: 0, time: 800 },
    'linear',
  );
  scene.showEffect(
    'rightchop',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 400 },
    { x: defender.leftof(-20), z: defender.behind(20), scale: 1, opacity: 0, time: 800 },
    'linear',
  );
  attacker.anim(
    { x: defender.leftof(20), y: defender.y, z: defender.behind(-20), time: 400 },
    'ballistic2Under',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, time: 50 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.delay(425);
  defender.anim(
    { x: defender.leftof(-15), y: defender.y, z: defender.behind(15), time: 50 },
    'swing',
  );
  defender.anim({ time: 300 }, 'swing');
};

/** PS `voltswitch` — an electro orb dashes in, the user darts out, then zaps. */
const voltswitch: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: defender.behind(-130), opacity: 0.8, time: 275 },
    { x: defender.x, y: defender.y, z: defender.z, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.behind(10), opacity: 0.7, scale: 0, time: 500 },
    { scale: 4, opacity: 0, time: 800 },
    'linear',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.7, scale: 0, time: 700 },
    { scale: 4, opacity: 0, time: 1000 },
    'linear',
  );

  attacker.anim({ z: attacker.behind(15), time: 200 }, 'decel');
  attacker.anim({ z: defender.behind(-170), time: 100 }, 'accel');
  attacker.anim({ z: attacker.z, time: 300 }, 'swing');
  defender.delay(500);
  defender.anim(
    { x: defender.leftof(5), y: defender.y, z: defender.behind(15), time: 50 },
    'swing',
  );
  defender.anim({ time: 300 }, 'swing');
};

/**
 * PS `volttackle` → alias of `wildcharge` — electro bursts + lightning + a hard
 * ramming lunge. PS drew two `attacker.sp` sprite-streaks (not vendored) — omitted;
 * the attacker's accel lunge preserves the ram read.
 */
const volttackle: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.7, time: 300 },
    { scale: 4, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.7, time: 500 },
    { scale: 4, opacity: 0, time: 800 },
    'linear',
  );
  scene.showEffect(
    'lightning',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    { x: defender.x, y: defender.y - 40, z: defender.behind(15), scale: 3, opacity: 0, time: 750 },
    'linear',
  );
  // PS drew two attacker-sprite streaks here (showEffect(attacker.sp, …)); omitted.
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `wakeupslap` — a sharp slap (right-then-left chop) + a contact lunge. */
const wakeupslap: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'rightchop',
    {
      x: defender.x + 30,
      y: defender.y,
      z: defender.behind(-10),
      scale: 0.6,
      opacity: 1,
      time: 400,
    },
    { x: defender.x - 10, y: defender.y - 10, xscale: 0, opacity: 0.5, time: 512.5 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leftchop',
    {
      x: defender.x,
      y: defender.y - 10,
      z: defender.behind(-10),
      scale: 0.6,
      xscale: 0,
      opacity: 1,
      time: 512.5,
    },
    { x: defender.x - 30, y: defender.y, xscale: 0.6, opacity: 0, time: 625 },
    'linear',
    'fade',
  );
  contactattack(scene, actors);
};

/** PS `watergun` → alias of `watersport` — four water globs lob onto the defender. */
const watergun: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.6 },
    { x: defender.x + 30, y: defender.y + 20, z: defender.z, scale: 1, opacity: 0.3 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.6, time: 75 },
    { x: defender.x + 20, y: defender.y - 20, z: defender.z, scale: 1, opacity: 0.3 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.6, time: 150 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 1, opacity: 0.3 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.6, time: 225 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.z, scale: 1, opacity: 0.3 },
    'ballistic',
    'explode',
  );
};

/** PS `waterpledge` → `hydroshot` template. */
const waterpledge: MoveAnim = hydroshot;

/** PS `waterpulse` — an icy bubble + a water wisp burst on the defender. */
const waterpulse: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.8 },
    { x: defender.x, y: defender.y, z: defender.behind(0), opacity: 0.7, time: 400 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3 },
    { x: defender.x, y: defender.y, z: defender.behind(0), opacity: 0.7, time: 400 },
    'decel',
    'explode',
  );
};

/** PS `watersport` — four water globs lob onto the defender. */
const watersport: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.6 },
    { x: defender.x + 30, y: defender.y + 20, z: defender.z, scale: 1, opacity: 0.3 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.6, time: 75 },
    { x: defender.x + 20, y: defender.y - 20, z: defender.z, scale: 1, opacity: 0.3 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.6, time: 150 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 1, opacity: 0.3 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.6, time: 225 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.z, scale: 1, opacity: 0.3 },
    'ballistic',
    'explode',
  );
};

/** PS `waterspout` — a blue wash, a rising waterspout off the user, then a downpour. */
const waterspout: MoveAnim = (scene: FxScene, [attacker, ...defenders]: Actor[]) => {
  for (const d of defenders) {
    d.delay(625);
    d.anim({ x: d.x - 30, time: 75 });
    d.anim({ x: d.x + 30, time: 100 });
    d.anim({ x: d.x - 30, time: 100 });
    d.anim({ x: d.x + 30, time: 100 });
    d.anim({ x: d.x, time: 100 });
  }
  const defender = defenders[1] || defenders[0];

  scene.backgroundEffect('#0000DD', 1100, 0.2);
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 1 },
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 6, opacity: 0 },
    'linear',
    'fade',
  );

  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y - 35, z: attacker.z, scale: 0.4, opacity: 0.6, time: 0 },
    { x: attacker.x + 30, y: attacker.y + 150, z: attacker.z, scale: 0.5, opacity: 0, time: 300 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y - 35, z: attacker.z, scale: 0.9, opacity: 0.6, time: 50 },
    { x: attacker.x - 30, y: attacker.y + 150, z: attacker.z, scale: 1, opacity: 0, time: 350 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y - 35, z: attacker.z, scale: 0.4, opacity: 0.6, time: 100 },
    { x: attacker.x - 10, y: attacker.y + 150, z: attacker.z, scale: 0.5, opacity: 0, time: 400 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y - 35, z: attacker.z, scale: 0.9, opacity: 0.6, time: 150 },
    { x: attacker.x + 10, y: attacker.y + 150, z: attacker.z, scale: 1, opacity: 0, time: 450 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y - 35, z: attacker.z, scale: 0.4, opacity: 0.6, time: 200 },
    { x: attacker.x + 35, y: attacker.y + 150, z: attacker.z, scale: 0.5, opacity: 0, time: 500 },
    'decel',
    'fade',
  );

  scene.showEffect(
    'waterwisp',
    {
      x: attacker.x,
      y: attacker.y + 175,
      z: attacker.behind(-90),
      scale: 0.4,
      opacity: 1,
      time: 300,
    },
    { x: defender.x + 45, y: defender.y + 20, z: defender.z, scale: 1.5, opacity: 0.7, time: 600 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    {
      x: attacker.x,
      y: attacker.y + 175,
      z: attacker.behind(-90),
      scale: 0.4,
      opacity: 1,
      time: 375,
    },
    { x: defender.x - 20, y: defender.y - 20, z: defender.z, scale: 1, opacity: 0.7, time: 675 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    {
      x: attacker.x,
      y: attacker.y + 175,
      z: attacker.behind(-90),
      scale: 0.4,
      opacity: 1,
      time: 450,
    },
    { x: defender.x + 30, y: defender.y, z: defender.z, scale: 1.5, opacity: 0.7, time: 750 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    {
      x: attacker.x,
      y: attacker.y + 175,
      z: attacker.behind(-90),
      scale: 0.4,
      opacity: 1,
      time: 525,
    },
    { x: defender.x - 40, y: defender.y + 5, z: defender.z, scale: 1, opacity: 0.7, time: 825 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    {
      x: attacker.x,
      y: attacker.y + 175,
      z: attacker.behind(-90),
      scale: 0.4,
      opacity: 1,
      time: 525,
    },
    { x: defender.x + 40, y: defender.y + 5, z: defender.z, scale: 1, opacity: 0.7, time: 825 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    {
      x: attacker.x,
      y: attacker.y + 175,
      z: attacker.behind(-90),
      scale: 0.4,
      opacity: 1,
      time: 575,
    },
    { x: defender.x - 70, y: defender.y + 5, z: defender.z, scale: 1.5, opacity: 0.7, time: 875 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    {
      x: attacker.x,
      y: attacker.y + 175,
      z: attacker.behind(-90),
      scale: 0.4,
      opacity: 1,
      time: 650,
    },
    { x: defender.x + 70, y: defender.y + 5, z: defender.z, scale: 1.5, opacity: 0.7, time: 950 },
    'accel',
    'explode',
  );
};

/**
 * PS `waterfall` → alias of `aquajet` — three rising water plumes + a fading-through
 * dash. PS drew two `attacker.sp` sprite-streaks (not vendored) — omitted; the
 * attacker's own fade-through lunge preserves the dash read.
 */
const waterfall: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'waterwisp',
    { x: attacker.x + 20, y: attacker.y + 30, z: defender.z, scale: 0, opacity: 1 },
    { y: attacker.y - 20, scale: 4, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'waterwisp',
    {
      x: Math.floor((attacker.x + defender.x) / 2) - 20,
      y: Math.floor((attacker.y + defender.y) / 2) + 30,
      z: Math.floor((attacker.z + defender.z) / 2),
      scale: 0,
      opacity: 1,
      time: 150,
    },
    { y: Math.floor((attacker.y + defender.y) / 2) - 20, scale: 4, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'waterwisp',
    { x: defender.x + 10, y: defender.y + 30, z: defender.z, scale: 0, opacity: 1, time: 300 },
    { y: defender.y - 20, scale: 4, opacity: 0 },
    'decel',
  );
  // PS drew two attacker-sprite streaks here (showEffect(attacker.sp, …)); omitted.
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(70), time: 300, opacity: 0.5 },
    'accel',
  );
  attacker.anim(
    { x: defender.x, y: defender.x, z: defender.behind(100), opacity: 0, time: 100 },
    'linear',
  );
  attacker.anim(
    { x: attacker.x, y: attacker.y, z: attacker.behind(70), opacity: 0, time: 1 },
    'linear',
  );
  attacker.anim({ opacity: 1, time: 500 }, 'decel');
  defender.delay(260);
  defender.anim({ z: defender.behind(30), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `wavecrash` — a deep-blue wash, two water bursts, and a ramming lunge. */
const wavecrash: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // PS used `linear-gradient(#000039 30%, #3848B8)`; our parser can't gradient —
  // substitute a solid deep-ocean-blue wash.
  scene.backgroundEffect('#1c2470', 600, 0.6);
  scene.showEffect(
    'waterwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 300 },
    { scale: 8, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'waterwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 500 },
    { scale: 8, opacity: 0, time: 800 },
    'linear',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `weatherball` — an orb floats up off the user, then drops onto the defender. */
const weatherball: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 1 },
    { y: attacker.y + 90, opacity: 0 },
    'linear',
  );
  scene.showEffect(
    'iceball',
    { x: defender.x, y: defender.y + 90, z: defender.z, scale: 0.5, opacity: 0, time: 500 },
    { y: defender.y, opacity: 1, time: 1000 },
    'linear',
    'explode',
  );
};

/** PS `whirlwind` — three rings of wisps swirl around the defender. */
const whirlwind: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  for (let i = 0; i < 3; i++) {
    scene.showEffect(
      'wisp',
      {
        x: defender.x + 30,
        y: defender.y - 35,
        z: defender.behind(i * 40 - 60),
        scale: 0.2,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x - 30,
        y: defender.y,
        z: defender.behind(i * 40 - 60),
        scale: 0.4,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'wisp',
      {
        x: defender.x - 30,
        y: defender.y + 35,
        z: defender.behind(i * 40 - 60),
        scale: 0.2,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x + 30,
        y: defender.y,
        z: defender.behind(i * 40 - 60),
        scale: 0.4,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'wisp',
      {
        x: defender.x + 30,
        y: defender.y,
        z: defender.behind(i * 40 - 60),
        scale: 0.2,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x - 30,
        y: defender.y - 35,
        z: defender.behind(i * 40 - 60),
        scale: 0.4,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'wisp',
      {
        x: defender.x - 30,
        y: defender.y,
        z: defender.behind(i * 40 - 60),
        scale: 0.2,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x + 30,
        y: defender.y - 35,
        z: defender.behind(i * 40 - 60),
        scale: 0.4,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
  }
};

/** PS `wideguard` → `selfstatus` template. */
const wideguard: MoveAnim = selfstatus;

/**
 * PS `wildcharge` — electro bursts + lightning + a hard ramming lunge. PS drew two
 * `attacker.sp` sprite-streaks (not vendored) — omitted; the attacker's accel lunge
 * preserves the ram read.
 */
const wildcharge: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.7, time: 300 },
    { scale: 4, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.7, time: 500 },
    { scale: 4, opacity: 0, time: 800 },
    'linear',
  );
  scene.showEffect(
    'lightning',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    { x: defender.x, y: defender.y - 40, z: defender.behind(15), scale: 3, opacity: 0, time: 750 },
    'linear',
  );
  // PS drew two attacker-sprite streaks here (showEffect(attacker.sp, …)); omitted.
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `willowisp` — a blue flame curls toward the defender and circles it. */
const willowisp: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'bluefireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0 },
    {
      x: defender.leftof(40),
      y: defender.y + 15,
      z: defender.z,
      scale: 0.8,
      opacity: 0.7,
      time: 500,
    },
    'decel',
    'fade',
  );
  scene.showEffect(
    'bluefireball',
    {
      x: defender.leftof(40),
      y: defender.y + 15,
      z: defender.z,
      scale: 0.8,
      opacity: 0.7,
      time: 500,
    },
    { x: defender.leftof(-40), y: defender.y, z: defender.z, scale: 0.7, opacity: 1, time: 700 },
    'swing',
    'fade',
  );
  scene.showEffect(
    'bluefireball',
    { x: defender.leftof(-40), y: defender.y, z: defender.z, scale: 0.1, opacity: 0, time: 700 },
    {
      x: defender.leftof(10),
      y: defender.y - 15,
      z: defender.z,
      scale: 0.7,
      opacity: 1,
      time: 900,
    },
    'swing',
    'explode',
  );
};

/**
 * PS `wish` — a wisp rises into a starry sky (charge), then descends and bursts
 * (residual). PS used a remote `bg-space.jpg`; substitute a dim deep-blue night wash.
 */
const wish: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  // PS used url('…/fx/bg-space.jpg'); substitute a dim starry-night wash.
  scene.backgroundEffect('#0a0a2a', 600, 0.4);
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 1 },
    { y: attacker.y + 130, opacity: 0 },
    'accel',
  );
};

const wishResidual: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  // PS used url('…/fx/bg-space.jpg'); substitute a dim starry-night wash.
  scene.backgroundEffect('#0a0a2a', 600, 0.4);
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y + 130, z: attacker.z, scale: 1, opacity: 0 },
    { x: attacker.x, y: attacker.y, z: attacker.z, opacity: 1 },
    'decel',
    'explode',
  );
  scene.timeOffset += 500;
};

/** PS `withdraw` — a shell glints as the user shrinks into it. */
const withdraw: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'shell',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.5, time: 0 },
    { scale: 0.8, opacity: 0.8, time: 400 },
    'linear',
    'fade',
  );
  attacker.anim({ scale: 0.4, opacity: 0, time: 400 }, 'linear');
  attacker.delay(75);
  attacker.anim({ x: attacker.x, time: 75 });
};

/** PS `wonderroom` → `dance` template. */
const wonderroom: MoveAnim = dance;

/** PS `woodhammer` — energy orbs + a spray of leaves, then a contact lunge. */
const woodhammer: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 420 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 520 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  scene.showEffect(
    'leaf1',
    { x: defender.x, y: defender.y - 40, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { x: defender.x, y: defender.y + 10, z: defender.z, scale: 2, opacity: 0.4, time: 700 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'leaf2',
    { x: defender.x - 40, y: defender.y - 40, z: defender.z, scale: 1, opacity: 1, time: 600 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 2, opacity: 0.4, time: 800 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'leaf2',
    { x: defender.x + 40, y: defender.y - 40, z: defender.z, scale: 1, opacity: 1, time: 700 },
    { x: defender.x + 40, y: defender.y, z: defender.z, scale: 2, opacity: 0.4, time: 900 },
    'decel',
    'fade',
  );
  contactattack(scene, actors);
};

/** PS `workup` → alias of `bulkup` — a charging wisp + a bracing scale-up. */
const workup: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y + 20, z: attacker.z, scale: 0.5, opacity: 0.1, time: 500 },
    {
      x: attacker.leftof(-30),
      y: attacker.y + 20,
      z: attacker.behind(-50),
      scale: 1,
      opacity: 0.3,
      time: 800,
    },
    'ballistic2Under',
    'fade',
  );
  attacker.anim({ y: attacker.y + 4, scale: 1.15, time: 200 }, 'linear');
  attacker.delay(300);
  attacker.anim({ time: 200 }, 'decel');
};

/** PS `worryseed` — a seed-orb lobs onto the defender and bursts. */
const worryseed: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.4, opacity: 1, time: 400 },
    'ballistic',
    'explode',
  );
};

/** PS `wrap` → alias of `bind` — icy coils squeeze the defender over a double lunge. */
const wrap: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'iceball',
    {
      x: defender.x,
      y: defender.y + 15,
      z: defender.z,
      scale: 0.7,
      xscale: 2,
      opacity: 0.3,
      time: 500,
    },
    { scale: 0.4, xscale: 1, opacity: 0.1, time: 1100 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'iceball',
    {
      x: defender.x,
      y: defender.y - 5,
      z: defender.z,
      scale: 0.7,
      xscale: 2,
      opacity: 0.3,
      time: 550,
    },
    { scale: 0.4, xscale: 1, opacity: 0.1, time: 1150 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'iceball',
    {
      x: defender.x,
      y: defender.y - 20,
      z: defender.z,
      scale: 0.7,
      xscale: 2,
      opacity: 0.3,
      time: 600,
    },
    { scale: 0.4, xscale: 1, opacity: 0.1, time: 1200 },
    'decel',
    'fade',
  );
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ y: defender.y + 15, z: defender.behind(10), yscale: 1.3, time: 200 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.delay(25);
  defender.anim(
    { x: defender.leftof(-10), y: defender.y + 15, z: defender.behind(5), yscale: 1.3, time: 200 },
    'swing',
  );
  defender.anim({ time: 200 }, 'swing');
};

/** PS `wringout` → alias of `forcepalm` — a hard palm chop + a grappling lunge. */
const wringout: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'rightchop',
    { x: defender.x, y: defender.y, z: defender.z - 10, scale: 0.6, opacity: 0.1, time: 350 },
    { x: defender.x + 20, scale: 0.5, opacity: 1, time: 500 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightchop',
    { x: defender.x + 20, y: defender.y, z: defender.z - 10, scale: 0.5, opacity: 1, time: 500 },
    { x: defender.x - 15, opacity: 0, time: 800 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'rightchop',
    { x: defender.x + 20, y: defender.y, z: defender.z - 10, scale: 0.5, opacity: 0.3, time: 575 },
    { x: defender.x - 15, opacity: 0, time: 850 },
    'decel',
    'fade',
  );
  attacker.anim(
    { x: defender.leftof(20), y: defender.y, z: defender.behind(-20), time: 400 },
    'ballistic2Under',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, time: 50 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.delay(425);
  defender.anim(
    { x: defender.leftof(-15), y: defender.y, z: defender.behind(15), time: 50 },
    'swing',
  );
  defender.anim({ time: 300 }, 'swing');
};

/** PS `xscissor` — a double ballistic strike, then two crossing slash marks. */
const xscissor: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 300 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');

  scene.showEffect(
    'rightslash',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 425 },
    { scale: 2, opacity: 0.5, time: 725 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightslash',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 625 },
    { scale: 3, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leftslash',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 625 },
    { scale: 3, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
};

/** PS `yawn` → `sound` template. */
const yawn: MoveAnim = sound;

/** PS `zapcannon` — a blue wash, an electro barrage, then shockwave + lightning bursts. */
const zapcannon: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#2630A9', 700, 0.6);
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6 },
    { x: defender.x + 30, y: defender.y + 30, z: defender.z, scale: 0.6, opacity: 0.3, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6, time: 75 },
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 0.6, opacity: 0.3, time: 275 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6, time: 150 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.3, time: 350 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6, time: 225 },
    { x: defender.x - 10, y: defender.y + 10, z: defender.z, scale: 0.6, opacity: 0.3, time: 425 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6, time: 300 },
    { x: defender.x + 10, y: defender.y - 10, z: defender.z, scale: 0.6, opacity: 0.3, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6, time: 375 },
    { x: defender.x - 20, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.3, time: 575 },
    'linear',
    'explode',
  );

  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 550 },
    { scale: 4, opacity: 0, time: 750 },
    'linear',
  );
  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 600 },
    { scale: 4, opacity: 0, time: 800 },
    'linear',
  );

  scene.showEffect(
    'lightning',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 550 },
    { scale: 4, opacity: 0, time: 750 },
    'linear',
  );
  scene.showEffect(
    'lightning',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 600 },
    { scale: 4, opacity: 0, time: 800 },
    'linear',
  );

  defender.delay(125);
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 150 }, 'swing');
};

/** PS `zenheadbutt` — a psychic mistball pulse, then a contact headbutt. */
const zenheadbutt: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'mistball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 450 },
    { scale: 2, opacity: 0, time: 700 },
    'linear',
  );
  contactattack(scene, actors);
};

/** PS `torment` → alias of `swagger` — a shudder on the user + angry marks rising on the defender. */
const torment: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  shake(scene, actors);
  scene.showEffect(
    'angry',
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 0.5, opacity: 0.5, time: 0 },
    { scale: 1, opacity: 1, time: 300 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'angry',
    { x: defender.x - 20, y: defender.y + 10, z: defender.z, scale: 0.5, opacity: 0.5, time: 100 },
    { scale: 1, opacity: 1, time: 400 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'angry',
    { x: defender.x, y: defender.y + 40, z: defender.z, scale: 0.5, opacity: 0.5, time: 200 },
    { scale: 1, opacity: 1, time: 500 },
    'ballistic2Under',
    'fade',
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * The batch-9 table, keyed by Showdown move id (toID form).
 * ──────────────────────────────────────────────────────────────────────── */

export const SHOWDOWN_BATCH_9: Record<string, MoveAnim> = {
  terrainpulse,
  thief,
  thrash,
  throatchop,
  thunderfang,
  thundershock,
  thunderwave,
  tickle,
  tidyup,
  torment,
  toxic,
  toxicspikes,
  toxicthread,
  trailblaze,
  transform,
  triattack,
  trick,
  trickroom,
  tripleaxel,
  triplekick,
  trumpcard,
  twinbeam,
  twineedle,
  twister,
  uturn,
  upperhand,
  uproar,
  vacuumwave,
  venomdrench,
  venoshock,
  vinewhip,
  vitalthrow,
  voltswitch,
  volttackle,
  wakeupslap,
  watergun,
  waterpledge,
  waterpulse,
  watersport,
  waterspout,
  waterfall,
  wavecrash,
  weatherball,
  whirlpool: watersport,
  whirlwind,
  wideguard,
  wildcharge,
  willowisp,
  wish,
  withdraw,
  wonderroom,
  woodhammer,
  workup,
  worryseed,
  wrap,
  wringout,
  xscissor,
  yawn,
  zapcannon,
  zenheadbutt,
};

/** Suppress unused-warning for the wish residual variant (kept for future wiring). */
void wishResidual;
